import { Camera } from '@babylonjs/core/Cameras/camera';
import { BoundingBox } from '@babylonjs/core/Culling/boundingBox';
import { MultiMaterial } from '@babylonjs/core/Materials/multiMaterial';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3, Color4, Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths';
import { CreatePlane } from '@babylonjs/core/Meshes/Builders/planeBuilder';
import { SphereBuilder as BabylonSphereBuilder } from '@babylonjs/core/Meshes/Builders/sphereBuilder';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { LinesMesh } from '@babylonjs/core/Meshes/linesMesh';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import '@babylonjs/core/Rendering/outlineRenderer';
import { UtilityLayerRenderer } from '@babylonjs/core/Rendering/utilityLayerRenderer';
import { Scene } from '@babylonjs/core/scene';
import { eAxis, eCylinderCap, eRenderMode } from '@models/classes/enums';
import { getByDynamicId } from '@models/classes/kb3d-manager';
import { IUvData } from '@models/classes/kb3d-models';
import { Kb3dObject } from '@models/classes/kb3d-object';
import { MaterialNode } from '@models/classes/materials/material-node';
import {
    MeshGroupNode,
    SketchNode,
    SpaceNode,
    TextNode,
    isBoxMesh,
    isConeMesh,
    isCylinderMesh,
    isDiscMesh,
    isPlaneMesh,
    isSketchNode,
    isSphereMesh,
    isTextNode,
    isTorusMesh,
} from '@models/classes/meshes';
import { CustomMeshNode } from '@models/classes/meshes/custom-mesh-node';
import { MeshNode } from '@models/classes/meshes/mesh-node';
import {
    isCustomMeshNode,
    isMeshGroupNode,
    isMeshNode,
    isPrimitiveMeshNode,
} from '@models/classes/meshes/mesh-utilities';
import { PrimitiveMeshNode } from '@models/classes/meshes/primitive-mesh-node';
import { KbColor, KbGeometry } from '@models/classes/primitives';
import { KbQuaternion } from '@models/classes/primitives/kb-quaternion';
import { KbVector } from '@models/classes/primitives/kb-vector';
import { SceneNode, isSceneNode } from '@models/classes/scene-node';
import { Token } from '@models/classes/token';
import { isDefined } from '@models/classes/utilities';
import { eParentChangeBehavior } from '@models/interfaces/settings';
import { ITrackedClass, isTrackedClass } from '@models/interfaces/tracked-class';
import { SettingsManagerService } from '@models/services/settings-manager-service';
import { eSideOrientationBabylonMap } from '@view/helpers/enum-maps';
import { SubmeshVertexData, verifyGeometry } from '@view/helpers/geometry-helper';
import { toRadians } from '@view/helpers/math-helper';
import { CylinderBuilder } from '@view/helpers/mesh-builders/cylinder-builder';
import { DiscBuilder } from '@view/helpers/mesh-builders/disc-builder';
import { PlaneBuilder } from '@view/helpers/mesh-builders/plane-builder';
import { SphereBuilder } from '@view/helpers/mesh-builders/sphere-builder';
import { TorusBuilder } from '@view/helpers/mesh-builders/torus-builder';
import { TextMeshFont, TextMeshVertexData } from '@view/helpers/text-mesh-helper';
import { KbViewer } from '@view/kb-viewer';
import {
    axisToVector3,
    calculateFaceNormal,
    fillSketchPolygon,
    parseSketchGeometry,
} from '@view/modules/sketching/util';
import { parse as fontParse } from 'opentype.js';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { Renderer } from './renderer';
import { RendererManager } from './renderer-manager';
import { ShadowManager } from './shadow-manager';

const primitiveShapeProperties = new Set([
    'height',
    'width',
    'depth',
    'radius',
    'radiusTop',
    'radiusBottom',
    'thickness',
    'arc',
    'latitudeStart',
    'latitudeSlice',
    'tessellation',
    'subdivisions',
    'capMode',
    'segments',
]);

const PIVOT_GIZMO_SCALE = 0.33;

const capModeMap: Record<eCylinderCap, number> = {
    [eCylinderCap.both]: Mesh.CAP_ALL,
    [eCylinderCap.start]: Mesh.CAP_START,
    [eCylinderCap.end]: Mesh.CAP_END,
    [eCylinderCap.none]: Mesh.NO_CAP,
};

type meshNodeSharedChanges = ITrackedClass<PrimitiveMeshNode & CustomMeshNode>['changes'];
const textMeshFonts = new Map<string, TextMeshVertexData>();

// const lineColor = new Color4(1, 1, 1);
// const selectedLineColor = new Color4(1, 0, 0);

export class MeshRenderer extends Renderer<SpaceNode, TransformNode | AbstractMesh> {
    utilityLayer: UtilityLayerRenderer;
    disposed = new Subject<void>();
    pivotGizmos = new Map<string, Mesh>();

    sketchPreviews = new Map<string, LinesMesh>();
    sketchTracings = new Map<string, LinesMesh>();
    sketchPlanePreviews = new Map<string, Mesh>();

    private renderMode: eRenderMode;

    constructor(
        viewer: KbViewer,
        scene: Scene,
        private rendererManager: RendererManager,
        private shadowManager: ShadowManager
    ) {
        super(viewer, scene);
        this.utilityLayer = viewer.utilityLayer;

        const tempVector = new Vector3();
        viewer.afterRender.pipe(takeUntil(this.disposed)).subscribe(() => {
            let dist: number;
            const camera = this.utilityLayer.utilityLayerScene.activeCamera!;

            this.pivotGizmos.forEach(gizmo => {
                if (camera.mode === Camera.ORTHOGRAPHIC_CAMERA) {
                    dist = ((camera.orthoRight || 0) - (camera.orthoLeft || 0)) / 2;
                } else {
                    const cameraPosition = camera.globalPosition;
                    gizmo.position.subtractToRef(cameraPosition, tempVector);
                    dist = tempVector.length() * PIVOT_GIZMO_SCALE;
                }

                gizmo.scaling.set(dist, dist, dist);
            });
        });
    }

    modelToken = Token.SpaceNode;
    handlesNode(node: Kb3dObject) {
        return isMeshNode(node) || isMeshGroupNode(node) || (isSceneNode(node) && node.isNested());
    }

    setRenderMode(mode: eRenderMode, sceneNode: SceneNode) {
        this.renderMode = mode;
        this.babylonMap.forEach(mesh => {
            if (mesh instanceof Mesh) {
                this.setRenderModeOnMesh(mesh, sceneNode);
            }
        });
    }

    dispose() {
        super.dispose();
        this.pivotGizmos.forEach(gizmo => {
            gizmo.dispose();
        });
        this.pivotGizmos.clear();
        this.disposed.next();
    }

    loadNewGeometry(node: CustomMeshNode) {
        node._loading = node.geometryId;
        const meshService = node._manager!.getService(Token.MeshService);
        meshService!.hydrateGeometry(node).then(() => {
            node._loading = null;
            this.invalidateBoundingBox(node);
        });
    }

    parentsVisible(node: SpaceNode) {
        while (node._parent && node._parent instanceof SpaceNode) {
            if (!node._parent.visible) {
                return false;
            }
            node = node._parent;
        }
        return true;
    }

    update3dInternal(node: ITrackedClass<SpaceNode>) {
        const isNewMesh = !this.get3d(node);
        if (isCustomMeshNode(node)) {
            if (
                node.visible &&
                this.parentsVisible(node) &&
                node.hasGeometry &&
                !node._geometry &&
                !node.geometryLoaded &&
                !node._loading
            ) {
                this.loadNewGeometry(node);
            }
        }

        const mesh = this.createOrUpdateMesh(node);

        const pivotGizmo = this.pivotGizmos.get(node._dynamicId);
        if (node._selected && mesh instanceof Mesh && !this.viewer.highlighter.has(node)) {
            this.viewer.highlighter.add(node, KbColor.Red());
            if (pivotGizmo) {
                pivotGizmo.setEnabled(true);
            }
        } else if (!node._selected && mesh instanceof Mesh && this.viewer.highlighter.has(node)) {
            this.viewer.highlighter.remove(node);
            if (pivotGizmo) {
                pivotGizmo.setEnabled(false);
            }
        }

        if (node instanceof SketchNode) {
            if (node._selected && node.visible) {
                const oldPreview = this.sketchPlanePreviews.get(node.id);
                if (oldPreview) {
                    oldPreview.dispose();
                }
                const mesh = this.createSketchPlaneVisualization(node);
                if (node.visible && mesh) {
                    this.sketchPlanePreviews.set(node.id, mesh);
                } else {
                    mesh?.dispose();
                    this.sketchPlanePreviews.delete(node.id);
                }
            } else if (!node._selected || !node.visible) {
                const SketchPlancePreviewMesh = this.sketchPlanePreviews.get(node.id);
                SketchPlancePreviewMesh?.dispose();
                this.sketchPlanePreviews.delete(node.id);
            }
        }

        if (!mesh) {
            throw Error('Could not create mesh');
        }

        if (isNewMesh || node.changes.has('_parent')) {
            this.updateParentMeshFromNode(node, mesh, isNewMesh);
        }
    }

    rebuildBaseGeometry(node: SpaceNode, preTransformChanged = false) {
        const mesh = this.get3d(node);
        if (mesh && mesh instanceof Mesh) {
            let meshVertexData: SubmeshVertexData | null = null;

            if (isPrimitiveMeshNode(node)) {
                const newMesh = this.createPrimitiveMesh(node);
                meshVertexData = SubmeshVertexData.ExtractFromMesh(newMesh);
                newMesh.dispose();

                if (isDefined(node.uvOverrides)) {
                    this.setBabylonUvData(node.uvOverrides, meshVertexData);
                }

                if (node instanceof MeshNode && meshVertexData) {
                    // Always pretransform primitives as they are always rebuilt from scratch
                    this.preTransformVertices(meshVertexData, node);
                }
            } else if (isCustomMeshNode(node)) {
                // If custom geometry does not have a geometry, don't do anything. Return null.
                if (node._geometry && node._geometry.positions) {
                    meshVertexData = this.getMeshVertexData(node);
                }
            } else if (isSketchNode(node)) {
                meshVertexData = this.buildSketchGeometry(node);
            } else if (isTextNode(node) && isTrackedClass(node)) {
                meshVertexData = this.buildTextGeometry(node);
            } else {
                throw Error('geometry needs to be recalculated but mesh is not a valid primitive or custom mesh');
            }

            return meshVertexData;
        }

        return null;
    }

    private buildSketchGeometry(node: SketchNode) {
        const result = new SubmeshVertexData();
        const positionVectors: Vector3[] = [];
        let pathSegmentIndices: number[][] = [];

        // Keep track of the path groups so we can match the hole paths with the groups in the vertex data
        // The key is the path group key, and the value is the indices of the generated polygons that are part of that path
        // One connected path may have multiple sketch paths because it requires different types of curves
        const pathGroupKeys = new Map<string, number>();
        const holePathsReferencingKeys: string[] = [];

        let currentPath: number[] | undefined;
        let lastPoint: Vector3 | undefined;
        for (let i = 0; i < node.paths.length; i++) {
            const path = node.paths[i];

            if (path._points && path._points.length > 0) {
                let thisPoint: Vector3 | undefined;

                for (let iP = 0; iP < path._points.length; iP++) {
                    const { x, y, z } = path._points[iP];
                    thisPoint = new Vector3(x, y, z);

                    // Separate paths that don't have a shared start/end to different logical paths
                    if (!lastPoint || !thisPoint.equals(lastPoint)) {
                        if (iP === 0 || !currentPath) {
                            if (!pathGroupKeys.has(path.pathGroupKey)) {
                                pathGroupKeys.set(path.pathGroupKey, pathSegmentIndices.length);
                            }
                            if (path.holeForPathGroup) {
                                holePathsReferencingKeys[pathSegmentIndices.length] = path.holeForPathGroup;
                            }
                            currentPath = [];
                            pathSegmentIndices.push(currentPath);
                        }

                        currentPath.push(positionVectors.length);
                        positionVectors.push(thisPoint);
                    }

                    lastPoint = thisPoint;
                }
            }
        }

        if (positionVectors.length === 0 || pathSegmentIndices.length === 0) {
            return null;
        }

        const positions: number[] = [];
        const indices: number[] = [];
        const normals: number[] = [];
        const paths: number[] = [];

        if (node.flipPath) {
            pathSegmentIndices.reverse();
            pathSegmentIndices.forEach(segment => segment.reverse());
        }

        const primaryNormal = axisToVector3(node.normal);
        // /**
        //  * We want the sketch to always "face" in the positive direction of the axis for consistency
        //  * Otherwise it's indeterminate which way the sketch will face and makes the sketch hard to manage
        //  * because it depends on the handed-ness of the points (clockwise or counter-clockwise)
        //  */
        pathSegmentIndices.forEach(segment => {
            const segmentPositions: Vector3[] = [];
            for (let i = 0; i < segment.length; i++) {
                const point = positionVectors[segment[i]];
                segmentPositions.push(point);
            }

            // However, don't flip non-closed paths because it makes no sense and may be a drive curve
            const closedPath =
                (segmentPositions.length > 1 &&
                    segmentPositions[0].equalsWithEpsilon(segmentPositions[segmentPositions.length - 1])) ||
                node.closePath;
            if (closedPath) {
                const faceNormal = calculateFaceNormal(segmentPositions);
                // If the rotation handedness is opposite the primary sketch vector, then flip the sketch path
                if (Vector3.Dot(faceNormal, primaryNormal) < 0) {
                    segment.reverse();
                }
            }
        });

        // Parse the holes and match them to their parent polygons
        const holesByPathsWithIndices: number[][][] = [];
        pathSegmentIndices.forEach((segment, idx) => {
            if (!holePathsReferencingKeys[idx]) {
                return;
            }
            const parentPathIndex = pathGroupKeys.get(holePathsReferencingKeys[idx]);
            if (parentPathIndex === undefined) {
                return;
            }
            // This segment is a hole
            if (!holesByPathsWithIndices[parentPathIndex]) {
                holesByPathsWithIndices[parentPathIndex] = [];
            }
            holesByPathsWithIndices[parentPathIndex].push(segment);
        });

        if (node.addFace) {
            // We have to reorder the segment indices because the vertex position order gets modified by the face fill
            // Due to holes needing be be defined alongside the parent polygons
            const reorderedPathSegmentIndices: number[][] = [];
            let positionPointer = 0;
            pathSegmentIndices.forEach((segment, idx) => {
                // Skip holes in the loop since they are processed with their parent polygons
                if (holePathsReferencingKeys[idx]) {
                    return;
                }
                const segmentPositions: Vector3[] = [];
                const segmentIndices: number[] = [];
                for (let i = 0; i < segment.length; i++) {
                    const point = positionVectors[segment[i]];
                    segmentPositions.push(point);
                    segmentIndices.push(positionPointer);
                    positionPointer++;
                }
                reorderedPathSegmentIndices.push(segmentIndices);

                const holesPositions: Vector3[][] = [];
                if (holesByPathsWithIndices[idx]) {
                    for (let hi = 0; hi < holesByPathsWithIndices[idx].length; hi++) {
                        const hole = holesByPathsWithIndices[idx][hi];
                        const holeData: Vector3[] = [];
                        const holeIndices: number[] = [];
                        for (let i = 0; i < hole.length; i++) {
                            const point = positionVectors[hole[i]];
                            holeData.push(point);
                            holeIndices.push(positionPointer);
                            positionPointer++;
                        }
                        holesPositions.push(holeData);
                        reorderedPathSegmentIndices.push(holeIndices);
                    }
                }

                const newMeshVertices = fillSketchPolygon(
                    segmentPositions,
                    node.flipPath,
                    primaryNormal,
                    holesPositions
                );

                // Fix the indices to point at the right vertices
                const startIndex = positions.length / 3;
                for (let i = 0; i < newMeshVertices.indices.length; i++) {
                    newMeshVertices.indices[i] += startIndex;
                }

                indices.push(...newMeshVertices.indices);
                positions.push(...newMeshVertices.positions);
                normals.push(...newMeshVertices.normals);
            });

            //const normals = new Float32Array(positionVectors.length * 3);
            const uvs = new Float32Array(positionVectors.length * 2);
            uvs.fill(0);

            result.positions = new Float32Array(positions);
            result.indices = new Uint16Array(indices);
            result.normals = new Float32Array(normals);
            result.uvs = uvs;
            pathSegmentIndices = reorderedPathSegmentIndices;
        } else {
            const positions = new Float32Array(positionVectors.length * 3);
            for (let i = 0; i < positionVectors.length; i++) {
                const point = positionVectors[i];
                positions.set([point.x, point.y, point.z], i * 3);
            }
            result.positions = positions;
        }

        const indexMapToGrouped: number[] = [];
        let i = 0;
        pathSegmentIndices.forEach((segment, indexOfPath) => {
            // Skip holes since they are processed later
            if (holePathsReferencingKeys[indexOfPath]) {
                return;
            }
            indexMapToGrouped[indexOfPath] = i;
            for (let i = 1; i < segment.length; i++) {
                paths.push(segment[i - 1], segment[i]);
            }

            if (segment.length > 2 && node.closePath) {
                paths.push(segment[segment.length - 1], segment[0]);
            }
            i++;
        });

        const holes: number[] = [];
        pathSegmentIndices.forEach((segment, indexOfPath) => {
            // Add holes after the regular polys
            if (!holePathsReferencingKeys[indexOfPath]) {
                return;
            }
            const parentPathIndex = pathGroupKeys.get(holePathsReferencingKeys[indexOfPath]);
            if (parentPathIndex === undefined) {
                return;
            }

            holes.push(indexMapToGrouped[parentPathIndex], paths.length);
            for (let i = 1; i < segment.length; i++) {
                paths.push(segment[i - 1], segment[i]);
            }

            if (segment.length > 2 && node.closePath) {
                paths.push(segment[segment.length - 1], segment[0]);
            }
        });

        result.paths = new Uint16Array(paths);
        result.holes = new Uint16Array(holes);
        node._sketchPoints = new Float32Array(result.positions);
        let counter = -1;
        const startingPoints = [];
        //for loop building the starting points array which stores sketchControlPoints at the start of a line segment
        for (let i = 0; i < node.paths.length; i++) {
            const path = node.paths[i];
            let lastPoint: Vector3 | undefined;

            if (path._points && path._points.length > 0) {
                let thisPoint: Vector3 | undefined;

                for (let iP = 0; iP < path._points.length; iP++) {
                    const { x, y, z } = path._points[iP];
                    thisPoint = new Vector3(x, y, z);

                    startingPoints.push(counter);
                    if (!lastPoint || !thisPoint.equals(lastPoint)) {
                        if (iP === 0 || !currentPath) {
                            startingPoints.pop();
                        }
                    }

                    lastPoint = thisPoint;
                    counter++;
                }
            }
        }
        node._lineSegmentStartingPoints = startingPoints;
        node.calculatedBoundingBox = this.calculateSketchBoundingBox(node);

        return result;
    }

    private updateSketchPreview(
        sketchNode: ITrackedClass<SketchNode>,
        vertexData: SubmeshVertexData | null,
        mesh: AbstractMesh
    ) {
        const previewMesh = this.sketchPreviews.get(sketchNode._dynamicId);

        const { segments } = parseSketchGeometry(vertexData);

        if (segments.length > 0) {
            const baseColor = sketchNode._rootScene?.edgeColor
                ? sketchNode._rootScene.edgeColor.toColor4()
                : new Color4(1, 1, 1, 1);
            const colors: Color4[][] = [];

            segments.forEach(segmentPoints => {
                const segmentColors: Color4[] = [];
                colors.push(segmentColors);
                segmentPoints.forEach(() => {
                    segmentColors.push(baseColor);
                });
            });

            const lineColor = colors.length > 0 ? colors : undefined;

            if (
                previewMesh &&
                !sketchNode.changes.has('addFace') &&
                !sketchNode.changes.has('paths') &&
                !sketchNode.changes.has('closePath')
            ) {
                MeshBuilder.CreateLineSystem(
                    `${sketchNode._dynamicId}_preview`,
                    {
                        lines: segments,
                        instance: previewMesh,
                        colors: lineColor,
                        updatable: true,
                    },
                    this.scene
                );
            } else {
                if (previewMesh) {
                    previewMesh.dispose();
                }
                const newPreviewMesh = MeshBuilder.CreateLineSystem(
                    `${sketchNode._dynamicId}_preview`,
                    {
                        lines: segments,
                        colors: lineColor,
                        updatable: true,
                    },
                    this.scene
                );
                this.sketchPreviews.set(sketchNode._dynamicId, newPreviewMesh);
                newPreviewMesh.parent = mesh;
                newPreviewMesh.enableEdgesRendering();
                newPreviewMesh.edgesColor = baseColor;
                this.viewer.camera.maintainNodeScreenSize(newPreviewMesh, (dist: number, mode: number) => {
                    if (mode === Camera.PERSPECTIVE_CAMERA) {
                        newPreviewMesh.edgesWidth = dist * 0.25;
                    } else {
                        newPreviewMesh.edgesWidth = 4;
                    }
                });
            }
        } else if (previewMesh) {
            previewMesh.dispose();
            this.sketchPreviews.delete(sketchNode._dynamicId);
        }
    }

    private updateSketchTracing(
        sketchNode: ITrackedClass<SketchNode>,
        vertexData: SubmeshVertexData | null,
        mesh: AbstractMesh
    ) {
        const previewMesh = this.sketchTracings.get(sketchNode._dynamicId);
        const sketchColor = sketchNode.pathColor.toColor4();
        if (sketchNode.tracePath) {
            if (previewMesh) {
                previewMesh.dispose();
            }
            //create/update drawings
            const linePoints: Vector3[] = [];
            sketchNode.paths.forEach(path => {
                path._points.forEach(point => {
                    linePoints.push(new Vector3(point.x, point.y, point.z));
                });
            });
            if (sketchNode.closePath) {
                const point = sketchNode.paths[0]._points[0];
                linePoints.push(new Vector3(point.x, point.y, point.z));
            }
            const lines = Mesh.CreateLines('lines', linePoints, this.scene, true);
            lines.enableEdgesRendering();
            lines.edgesWidth = sketchNode.lineThickness;
            lines.edgesColor = sketchColor;
            this.sketchTracings.set(sketchNode._dynamicId, lines);
        } else {
            if (previewMesh || (previewMesh && sketchNode.tracePath === false)) {
                previewMesh.dispose();
                this.sketchTracings.delete(sketchNode._dynamicId);
            }
        }
    }

    private buildTextGeometry(node: ITrackedClass<TextNode>): SubmeshVertexData | null {
        const fontService = node._manager!.getService(Token.FontService);

        if (!node.fontFile || !node.text) {
            return null;
        }

        if (!fontService.readyFonts.has(node.fontFile)) {
            fontService.loadFontByFile(node.fontFile).then(resultData => {
                const fontBuffer = resultData;
                if (fontBuffer) {
                    node.setPropertyIsModified('fontFile', true);
                }
            });
            return null;
        } else {
            let vertexDataBuilder = textMeshFonts.get(node.fontFile);

            if (!vertexDataBuilder) {
                const fontBuffer = fontService.readyFonts.get(node.fontFile)!;
                const openTypeFont = fontParse(fontBuffer);
                const font = new TextMeshFont(node.fontFile, openTypeFont, this.scene);
                vertexDataBuilder = new TextMeshVertexData(font);
                textMeshFonts.set(node.fontFile, vertexDataBuilder);
            }
            return vertexDataBuilder.getText(node.text, node.fontSize, node.charSpacing, node.curveRadius);
        }
    }

    getMeshGeometry(node: SpaceNode) {
        const mesh = this.get3d(node);
        if (mesh && mesh instanceof Mesh && mesh._geometry) {
            const geometry = SubmeshVertexData.ExtractFromMesh(mesh);
            // // Make sure the geometry is valid and actually has some geometry in it so consumers of this function don't have to deep check.
            // if(!geometry.indices || !geometry.positions || geometry.indices.length === 0) {
            //     return null;
            // }

            return geometry;
        }
        return null;
    }

    fetchMaterialNode(meshNode: MeshNode, materialId: string) {
        const localMaterial = meshNode._manager?.getById<MaterialNode>(materialId); //find the material if it's local

        return new Promise<MaterialNode>((resolve, reject) => {
            if (localMaterial) {
                //it's a local material
                resolve(localMaterial);
            } else {
                // Not a local material, so it's a global or library material
                this.rendererManager.material
                    .loadGlobalMaterial(materialId, meshNode._manager!.services)
                    .pipe(take(1))
                    .subscribe(globalLibMaterial => {
                        if (!globalLibMaterial) {
                            console.warn(
                                `Cannot find global material ${materialId} on node ${meshNode.name} (${meshNode.id})`
                            );
                            reject('could not load global material');
                        } else {
                            resolve(globalLibMaterial);
                        }
                    });
            }
        });
    }

    setRenderModeOnMesh(mesh: Mesh, sceneNode: SceneNode) {
        const mode = this.renderMode;
        if (mesh.geometry) {
            if (mode === eRenderMode.edge || mode === eRenderMode.hiddenEdges) {
                mesh.enableEdgesRendering(sceneNode.edgeDisplayTolerance);
                mesh.renderOutline = true;

                mesh.edgesWidth = sceneNode.edgeWidth;
                mesh.edgesColor = sceneNode.edgeColor.toColor4();
                mesh.outlineWidth = sceneNode.edgeWidth / 100;
                mesh.outlineColor = sceneNode.edgeColor.toColor3();

                // Another option for the hidden edges renderer but results in render artifacts especially when meshes go on top of each other.
                // if (mode === eRenderMode.hiddenEdges) {
                //     mesh.metadata = { ...mesh.metadata, visibility: mesh.visibility };
                //     mesh.visibility = 0.001;
                // }
            } else {
                // mesh.visibility = mesh.metadata?.visibility || 1;
                mesh.renderOutline = false;
                mesh.disableEdgesRendering();
            }
        }
    }

    private getMeshVertexData(node: CustomMeshNode) {
        if (!node._geometry) {
            throw Error('Cannot get vertex data because mesh has no geometry');
        }

        let meshVertexData = SubmeshVertexData.FromGeometry(node._geometry);
        meshVertexData = this.preTransformVertices(meshVertexData, node, node._geometry);

        return meshVertexData;
    }

    private preTransformVertices(meshVertexData: SubmeshVertexData, node: MeshNode, cachedGeometry?: KbGeometry) {
        if (node.preTransform && node.preTransform.length !== 16) {
            throw Error('invalid pretransform array ');
        }

        // If pretransform is not set on the node, the transform we want is the identity matrix (no change from original)
        const transform = node.preTransform ? Matrix.FromArray(node.preTransform) : Matrix.Identity();
        // Check if the geometry has a transform applied
        const existingTransform = cachedGeometry?._hasTransform;

        // If the geometry has already been transformed, then we have to undo that transform to get the correct transform
        // Starting with the invert matrix of the existing transform will cancel out the existing transform and result in the original mesh
        if (existingTransform) {
            Matrix.FromArray(existingTransform).invert().multiplyToRef(transform, transform);
        }

        // If the transform we want (taking the existing transform into account) is the identity matrix, do nothing and return the geometry
        if (!transform.isIdentity()) {
            if (existingTransform) {
                // If the geometry has already been transformed, don't overwrite the transform so we don't repeatedly transform it
                meshVertexData = meshVertexData.deepClone();
            } else if (cachedGeometry) {
                // If the geoemtry has no existing transform, set the hasTransform property since it will get mutated with the new transform.
                cachedGeometry._hasTransform = node.preTransform;
            }
            meshVertexData.transform(transform);
        }

        return meshVertexData;
    }

    private updateParentMeshFromNode(node: ITrackedClass<SpaceNode>, targetNode: TransformNode, isNewMesh: boolean) {
        const parentNode = node._parent;
        if (parentNode && parentNode instanceof SpaceNode) {
            const isRootScene = parentNode instanceof SceneNode && !parentNode.isNested();
            const parent3d = isRootScene
                ? this.rendererManager.sceneRenderer.get3d(parentNode as SceneNode)
                : this.get3d(parentNode);
            if (parent3d) {
                if (targetNode instanceof Mesh) {
                    targetNode.setParent(parent3d);
                } else {
                    targetNode.parent = parent3d;
                }
            } else {
                console.error(`Could not find mesh by ID ${parentNode.id} in scene!`);
            }
        } else {
            return; // if the node has no parent, it's not part of the scene anymore
        }

        if (
            isNewMesh ||
            SettingsManagerService.get('parentChangeBehavior') === eParentChangeBehavior.PRESERVE_PROPERTY
        ) {
            this.updateMeshTransform(node, targetNode, null);
        } else {
            node.position = KbVector.FromVec3(targetNode.position);

            if (targetNode.rotationQuaternion) {
                node.useEuler = false;
                node.setRotationQuaternion(
                    new KbQuaternion(
                        targetNode.rotationQuaternion.x,
                        targetNode.rotationQuaternion.y,
                        targetNode.rotationQuaternion.z,
                        targetNode.rotationQuaternion.w
                    )
                );
            } else if (node.eulerRotation) {
                node.useEuler = true;
                node.eulerRotation = new KbVector(targetNode.rotation.x, targetNode.rotation.y, targetNode.rotation.z);
            }

            node.scaling = targetNode.scaling.x;
        }
        this.invalidateBoundingBox(parentNode); //invalidate parent node in the bounding box cache as now it has a new child
    }

    private recursiveLoadChildMeshGeometry(node: SpaceNode) {
        const children = node.getChildren();
        children.forEach(child => {
            if (child instanceof SpaceNode && child.visible) {
                if (child instanceof CustomMeshNode && isTrackedClass(child)) {
                    if (child.hasGeometry && !child._geometry && !child.geometryLoaded && !child._loading) {
                        this.loadNewGeometry(child);
                    }
                } else {
                    this.recursiveLoadChildMeshGeometry(child);
                }
            }
        });
    }

    private createOrUpdateMesh<T extends TransformNode = TransformNode>(
        node: ITrackedClass<SpaceNode>,
        otherMesh?: Mesh
    ): T {
        let mesh: TransformNode | AbstractMesh | undefined;
        if (otherMesh) {
            mesh = otherMesh;
        } else {
            mesh = this.get3d(node);
        }
        const changes = node.changes as meshNodeSharedChanges;
        let allChanges = false;
        let meshVerticesNeedReset = false;
        if (!mesh) {
            // New Mesh

            allChanges = true;
            if (isMeshNode(node)) changes.set('features', node.features);

            if (isMeshGroupNode(node) || isSceneNode(node)) {
                mesh = new TransformNode(node._dynamicId, this.scene);
            } else {
                const newMesh = (mesh = new Mesh(node._dynamicId, this.scene));
                if (isCustomMeshNode(node) && isTrackedClass(node) && node._geometry) {
                    this.setBabylonVertexData(node, newMesh);
                } else {
                    meshVerticesNeedReset = true;
                }
            }
            mesh.scaling = Vector3.One();
            mesh.name = node.name;
            mesh.id = node._dynamicId;

            this.set3d(node, mesh);
        } else if (isCustomMeshNode(node) && changes.has('geometryId') && node._loading !== node.geometryId) {
            // Only load new geometry if the geometry hasn't just changed
            this.loadNewGeometry(node);
        } else if (isCustomMeshNode(node) && isTrackedClass(node) && node._geometry && node.changes.has('_geometry')) {
            // If we're changing the geometry, we always need to (re)apply all mesh properties
            // to the mesh as the new geometry won't have the right properties
            allChanges = true;
            changes.set('features', node.features);
        }

        if (
            allChanges ||
            changes.hasAny(
                'position',
                'useEuler',
                'eulerRotation',
                'rotationAxis',
                'rotationAngle',
                'pivot',
                'scaling',
                '_showPivot'
            )
        ) {
            this.updateMeshTransform(node, mesh, allChanges ? null : (changes as ITrackedClass<SpaceNode>['changes']));
            if (isMeshNode(node)) {
                this.rendererManager.material.reflectedMeshes.set(node._dynamicId, mesh as AbstractMesh);
            }
            this.rendererManager.material.updateReflectionProbes();
        }

        if (changes.has('scaling')) {
            this.updateConnectorScaling(node);
        }

        const pivotGizmo = this.pivotGizmos.get(node._dynamicId);

        if (allChanges || changes.has('visible')) {
            mesh.setEnabled(!!node.visible);
            this.viewer.polygonTracker.next();
            this.viewer.warningTracker.next();

            if (pivotGizmo) {
                pivotGizmo.setEnabled(!!node.visible);
            }
            this.invalidateBoundingBox(node);

            // If a group node is made visible and it has a child node that has geometry load it now
            if (node.visible && node instanceof MeshGroupNode) {
                let parentNode = node._parent;
                let allParentsVisible = true;
                while (parentNode) {
                    if (!parentNode.visible) {
                        allParentsVisible = false;
                        break;
                    }
                    parentNode = parentNode._parent;
                }
                if (allParentsVisible) {
                    this.recursiveLoadChildMeshGeometry(node);
                }
            }
        }

        if (mesh instanceof Mesh) {
            const meshNode = node as ITrackedClass<MeshNode>;
            mesh.isBlocker = false;

            if (allChanges || changes.has('receiveShadows')) {
                mesh.receiveShadows = !!meshNode.receiveShadows;
                this.shadowManager.toggleReceiver(mesh, !!meshNode.receiveShadows);
            }

            if (changes.has('isPickable')) {
                if (meshNode.isPickable !== undefined) {
                    mesh.isPickable = meshNode.isPickable;
                } else {
                    mesh.isPickable = true;
                }
            }

            if (allChanges || changes.has('castShadows')) this.shadowManager.toggleCaster(mesh, !!meshNode.castShadows);
            if (allChanges || changes.has('checkCollisions')) mesh.checkCollisions = !!meshNode.checkCollisions;
            if (allChanges || changes.has('flipSurfaceOrientation')) {
                if (meshNode.flipSurfaceOrientation) {
                    mesh.overrideMaterialSideOrientation = Mesh.BACKSIDE;
                } else {
                    mesh.overrideMaterialSideOrientation = Mesh.FRONTSIDE;
                }
            }

            if (this.renderMode !== eRenderMode.hiddenEdges) {
                if (allChanges || changes.has('opacity')) {
                    mesh.visibility = meshNode.opacity == null ? 1 : meshNode.opacity;
                }
            }

            const warningTracker = {
                name: meshNode.name,
                isEnabled: meshNode.visible,
                castShadows: meshNode.castShadows || false,
                receiveShadows: meshNode.receiveShadows || false,
                polygonNumber: meshNode._polygonCount || 0,
                featureNumber: node.features.length,
            };
            this.viewer.MeshWarningTracker.set(mesh.id, warningTracker);
            // Material
            if (allChanges || changes.has('materialId') || changes.has('visible')) {
                const _mesh = mesh;

                // If the material is a multimaterial, make sure all the material nodes making it up are initialized
                if (meshNode.visible && mesh.material instanceof MultiMaterial) {
                    mesh.material.subMaterials.forEach(material => {
                        if (material) {
                            const localMaterial = getByDynamicId<MaterialNode>(material.id, false);

                            if (localMaterial) {
                                localMaterial.init();
                            } else {
                                // Global material ?
                                const globalMaterial = this.rendererManager.material.getGlobalMaterial(material.id);
                                if (globalMaterial) {
                                    globalMaterial.init();
                                }
                            }
                        }
                    });
                }

                if (meshNode.materialId) {
                    this.fetchMaterialNode(meshNode, meshNode.materialId).then(
                        materialNode => {
                            if (meshNode.visible) {
                                this.setMaterialOnMesh(meshNode, _mesh, materialNode);
                            }
                        },
                        () => {
                            meshNode.materialId = undefined;
                        }
                    );
                } else {
                    if (mesh.material instanceof MultiMaterial) {
                        mesh.material.subMaterials[0] = this.rendererManager.material.defaultMaterial;
                        mesh.material.forceCompilation(mesh); // If you don't do this webgl vomits errors because we invalidated the cache
                    } else {
                        mesh.material = this.rendererManager.material.defaultMaterial;
                    }
                }

                if (isMeshNode(node)) {
                    this.rendererManager.material.reflectedMeshes.set(node._dynamicId, mesh);
                }
                this.rendererManager.material.updateReflectionProbes();
            }

            let sketchNode: ITrackedClass<SketchNode> | undefined;
            let updateSketchDrawing = false;

            // For primitives, recalculate the mesh if the shape properties changed
            if (isPrimitiveMeshNode(meshNode)) {
                if (allChanges) {
                    meshVerticesNeedReset = true;
                } else {
                    meshNode.changes.forEach((_, key) => {
                        if (primitiveShapeProperties.has(key)) {
                            meshVerticesNeedReset = true;
                        }
                    });
                }
            } else if (isCustomMeshNode(meshNode) && isTrackedClass(meshNode)) {
                meshVerticesNeedReset = meshNode.changes.has('_geometry') && !!meshNode._geometry;
            } else if (isSketchNode(meshNode) && isTrackedClass(meshNode)) {
                sketchNode = meshNode;
                meshVerticesNeedReset = sketchNode.changes.hasAny(
                    'paths',
                    'addFace',
                    'flipPath',
                    'clonePath',
                    'closePath',
                    '_previewVisible'
                );

                updateSketchDrawing = sketchNode.changes.hasAny(
                    'paths',
                    'addFace',
                    'flipPath',
                    'clonePath',
                    'closePath',
                    '_previewVisible',
                    'lineThickness',
                    'pathColor',
                    'tracePath'
                );

                if (sketchNode.changes.hasAny('boundingXOverride', 'boundingYOverride', 'boundingZOverride')) {
                    sketchNode._boundingBoxChanges.next(sketchNode.getBoundingBox());
                }
            } else if (isTextNode(meshNode) && isTrackedClass(meshNode)) {
                meshVerticesNeedReset = meshNode.changes.hasAny(
                    'text',
                    'fontSize',
                    'curveRadius',
                    'charSpacing',
                    'fontFile'
                );
            }

            let preTransformChanged = false;
            if (allChanges || changes.hasAny('features', 'preTransform')) {
                meshVerticesNeedReset = true;
                preTransformChanged = !(allChanges || changes.has('_geometry')); // If geometry has changed, then the geometry was just loaded
            }

            // Features or shape properties have been changed, we have to recalculate original geometry and reprocess features on top
            if (allChanges || changes.has('uvOverrides') || meshVerticesNeedReset || updateSketchDrawing) {
                const meshVertexData = this.rebuildBaseGeometry(node, preTransformChanged);
                if (meshVertexData) {
                    // This call should also apply the new vertex data.
                    this.rendererManager.features.resetMeshGeometry(meshNode, null, meshVertexData);
                } else {
                    if (mesh.geometry) {
                        // If mesh geometry is set, the mesh was previously initialized but now is being generated with no geometry so we should destroy the old geometry
                        // Failing to do so will cause Babylon geometry issues
                        mesh.dispose();
                        this.delete3d(node);
                    }
                }

                if (sketchNode && sketchNode._previewVisible) {
                    this.updateSketchPreview(sketchNode, meshVertexData, mesh);
                }

                if (sketchNode && sketchNode.tracePath && updateSketchDrawing) {
                    if (sketchNode) {
                        this.updateSketchTracing(sketchNode, meshVertexData, mesh);
                    }
                }
            }

            // If this is a sketch, see if we need to show a preview of it
            if (sketchNode && sketchNode.changes.has('_previewVisible')) {
                const previewMesh = this.sketchPreviews.get(meshNode._dynamicId);
                if (previewMesh) {
                    previewMesh.setEnabled(sketchNode._previewVisible);
                }
            }
            this.viewer.meshWarningsNeedsUpdate.next();
        }
        return mesh as unknown as T;
    }

    private updateMeshTransform<T extends TransformNode = TransformNode>(
        node: SpaceNode,
        mesh: T,
        changes: ITrackedClass<SpaceNode>['changes'] | null // null means all changes
    ): T {
        const pivot = node.pivot.toVec3();
        const originOffset = pivot.scale(-1);

        const newPosition = node.position.toVec3();

        let newRotation: Quaternion;
        if (node.useEuler) {
            newRotation = Quaternion.FromEulerAngles(
                toRadians(node.eulerRotation.x),
                toRadians(node.eulerRotation.y),
                toRadians(node.eulerRotation.z)
            );
        } else {
            newRotation = Quaternion.RotationAxis(node.rotationAxis.toVec3(), toRadians(node.rotationAngle));
        }
        const rotationMatrix = new Matrix();
        newRotation.toRotationMatrix(rotationMatrix);
        Vector3.TransformCoordinatesToRef(originOffset, rotationMatrix, originOffset);
        mesh.rotationQuaternion = newRotation;

        const scalingMatrix = Matrix.Scaling(node.scaling, node.scaling, node.scaling);
        Vector3.TransformCoordinatesToRef(originOffset, scalingMatrix, originOffset);
        mesh.scaling = new Vector3().setAll(node.scaling);

        newPosition.addInPlace(originOffset).addInPlace(pivot);

        if (!mesh.position.equals(newPosition)) {
            mesh.position = newPosition;
        }

        this.rendererManager.features.transformChanged(node);
        this.invalidateBoundingBox(!changes || changes.has('scaling') ? node : node._parent); //scaling, changes current node, but all others only change the parent

        if (node._showPivot) {
            let pivotGizmo = this.pivotGizmos.get(node._dynamicId);
            if (!pivotGizmo) {
                pivotGizmo = BabylonSphereBuilder.CreateSphere(
                    'pivotSphere',
                    { diameter: 0.05 },
                    this.utilityLayer.utilityLayerScene
                );

                const coloredMaterial = new StandardMaterial('', this.utilityLayer.utilityLayerScene);
                coloredMaterial.emissiveColor = Color3.Red();
                pivotGizmo.material = coloredMaterial;
                this.pivotGizmos.set(node._dynamicId, pivotGizmo);
            }
            if (!pivotGizmo.isEnabled()) {
                pivotGizmo.setEnabled(true);
            }

            const worldPivot = pivot.add(node.position.toVec3());
            if (mesh.parent) {
                const transform = mesh.parent.computeWorldMatrix(true);
                Vector3.TransformCoordinatesToRef(worldPivot, transform, worldPivot);
            }

            pivotGizmo.position = worldPivot;
        } else {
            const pivotGizmo = this.pivotGizmos.get(node._dynamicId);
            if (pivotGizmo && pivotGizmo.isEnabled()) {
                pivotGizmo.setEnabled(false);
            }
        }

        return mesh;
    }

    private createPrimitiveMesh(node: PrimitiveMeshNode): Mesh {
        if (!isPrimitiveMeshNode(node)) throw Error('Primitive not set');

        if (isBoxMesh(node)) {
            if (node.tessellation <= 1) {
                return MeshBuilder.CreateBox(
                    node._dynamicId,
                    {
                        height: node.height,
                        width: node.width,
                        depth: node.depth,
                        sideOrientation: eSideOrientationBabylonMap[node.sideOrientation],
                        updatable: true,
                    },
                    this.scene
                );
            } else {
                return MeshBuilder.CreateTiledBox(
                    node._dynamicId,
                    {
                        height: node.height,
                        width: node.width,
                        depth: node.depth,
                        sideOrientation: eSideOrientationBabylonMap[node.sideOrientation],
                        updatable: true,
                        tileSize: 1 / node.tessellation,
                    },
                    this.scene
                );
            }
        } else if (isSphereMesh(node)) {
            return SphereBuilder.CreateSphere(
                node._dynamicId,
                {
                    segments: node.tessellation,
                    diameter: node.radius * 2,
                    arc: node.arc / 360,
                    sliceStart: node.latitudeStart / 180,
                    slice: node.latitudeSlice / 180,
                    sideOrientation: eSideOrientationBabylonMap[node.sideOrientation],
                    updatable: true,
                },
                this.scene
            );
        } else if (isCylinderMesh(node)) {
            return CylinderBuilder.CreateCylinder(
                node._dynamicId,
                {
                    height: node.height,
                    diameter: node.radius * 2,
                    tessellation: node.tessellation,
                    subdivisions: node.subdivisions,
                    hasRings: node.subdivisions > 1,
                    cap: capModeMap[node.capMode],
                    sideOrientation: eSideOrientationBabylonMap[node.sideOrientation],
                    arc: node.arc / 360,
                    updatable: true,
                },
                this.scene
            );
        } else if (isConeMesh(node)) {
            return MeshBuilder.CreateCylinder(
                node._dynamicId,
                {
                    height: node.height,
                    diameterTop: node.radiusTop * 2,
                    diameterBottom: node.radiusBottom * 2,
                    tessellation: node.tessellation,
                    sideOrientation: eSideOrientationBabylonMap[node.sideOrientation],
                    updatable: true,
                },
                this.scene
            );
        } else if (isTorusMesh(node)) {
            return TorusBuilder.CreateTorus(
                node._dynamicId,
                {
                    diameter: node.radius * 2,
                    thickness: node.thickness,
                    tessellation: node.tessellation,
                    sideOrientation: eSideOrientationBabylonMap[node.sideOrientation],
                    arc: node.arc / 360,
                    sliceStart: node.latitudeStart / 360,
                    slice: node.latitudeSlice / 360,
                    updatable: true,
                },
                this.scene
            );
        } else if (isPlaneMesh(node)) {
            const mesh = PlaneBuilder.CreatePlane(
                node._dynamicId,
                {
                    width: node.width,
                    height: node.depth,
                    tileHeight: node.depth / node.segments,
                    tileWidth: node.width / node.segments,
                    sideOrientation: eSideOrientationBabylonMap[node.sideOrientation],
                    updatable: true,
                },
                this.scene
            );
            mesh.rotation.x = Math.PI / 2;
            mesh.bakeCurrentTransformIntoVertices();
            return mesh;
        } else if (isDiscMesh(node)) {
            return DiscBuilder.CreateDisc(
                node._dynamicId,
                {
                    radius: node.radius,
                    tessellation: node.tessellation,
                    sideOrientation: eSideOrientationBabylonMap[node.sideOrientation],
                    updatable: true,
                },
                this.scene
            );
        } else {
            throw Error('Mesh primitive not implemented');
        }
    }

    private setBabylonVertexData(kbMesh: CustomMeshNode, mesh: Mesh, preserveSubmeshes = true) {
        if (!kbMesh._geometry) {
            return;
        }
        const vertexData = this.getMeshVertexData(kbMesh);

        const invalid = verifyGeometry(vertexData);
        if (invalid) {
            console.error(`Invalid or corrupted geometry in mesh ${kbMesh.name}. The ${invalid} are invalid.`);
        } else {
            vertexData.applyToMesh(mesh, true, preserveSubmeshes);
            kbMesh._polygonCount = mesh.getTotalIndices() / 3;
            kbMesh._verticesCount = mesh.getTotalVertices();
            this.viewer.polygonTracker.next();
            this.viewer.warningTracker.next();
        }

        this.setRenderModeOnMesh(mesh, kbMesh._rootScene!);

        if (mesh.isFacetDataEnabled) {
            mesh.updateFacetData();
        }
    }

    private setBabylonUvData(uvData: IUvData, vertexData: VertexData) {
        if (uvData.uvs) {
            vertexData.uvs = uvData.uvs;
        }
        if (uvData.uv2s) {
            vertexData.uvs2 = uvData.uv2s;
        }
        if (uvData.uv3s) {
            vertexData.uvs3 = uvData.uv3s;
        }
        if (uvData.uv4s) {
            vertexData.uvs4 = uvData.uv4s;
        }
        if (uvData.uv5s) {
            vertexData.uvs5 = uvData.uv5s;
        }
        if (uvData.uv6s) {
            vertexData.uvs6 = uvData.uv6s;
        }

        return vertexData;
    }

    protected setMaterialOnMesh(meshNode: MeshNode, babylonMesh: Mesh, materialNode: MaterialNode) {
        if (meshNode.visible) {
            materialNode.init();
        }

        const material = this.rendererManager.material.get3d(materialNode);
        if (!material) {
            throw Error('Material could not be found in renderer. Has the material been created?');
        }

        // If the mesh already has a material and it's a multimaterial, then update the material on index 0
        // Which is reserved for the "base" material.
        if (babylonMesh.material instanceof MultiMaterial) {
            babylonMesh.material.subMaterials[0] = material;
            babylonMesh.material.forceCompilation(babylonMesh);
        } else if (!babylonMesh.material || babylonMesh.material.id != materialNode._dynamicId) {
            babylonMesh.material = material;
        }
    }

    protected invalidateBoundingBox(node?: SpaceNode) {
        if (node) {
            this.viewer.bbCache.invalidate(node);
        }
    }

    public delete3d(node: ITrackedClass<SpaceNode>) {
        if (isSketchNode(node)) {
            node._boundingBoxChanges.complete();
        }

        const bnode = this.get3d(node);
        if (bnode && bnode instanceof Mesh) {
            this.viewer.MeshWarningTracker.delete(bnode.id);
            this.shadowManager.removeCaster(bnode);
            this.shadowManager.removeReceiver(bnode);
            if (bnode.material instanceof MultiMaterial) {
                bnode.material.dispose(true, true, false); // multimaterials are 1:1 with meshes
            }
        }
        this.rendererManager.material.reflectedMeshes.delete(node._dynamicId);
        this.rendererManager.material.updateReflectionProbes();
        this.viewer.meshWarningsNeedsUpdate.next();
        super.delete3d(node);
        this.viewer.polygonTracker.next();
        this.viewer.warningTracker.next();
    }

    public createSketchPlaneVisualization(sketch: SketchNode) {
        const color = Color3.Yellow();

        const boundingBox = sketch.calculatedBoundingBox;
        let squareSize = 3;
        let calculatedSize = 0;
        let boundingBoxCenter = new Vector3(0, 0, 0);
        if (boundingBox) {
            boundingBoxCenter = boundingBox.center.clone();
            calculatedSize = boundingBox.minimum.subtract(boundingBox.maximum).length();
        }
        if (calculatedSize > squareSize) {
            squareSize = calculatedSize * 1;
        }
        const coloredMaterial = new StandardMaterial('', this.scene);
        coloredMaterial.wireframe = true;
        coloredMaterial.emissiveColor = color;
        const sketchPlaneVisualization = CreatePlane('mirrorPlane', { size: squareSize }, this.scene);
        const sketchMesh = this.viewer.getRendererNode(sketch);
        if (sketchMesh) {
            const translation = sketchMesh.computeWorldMatrix();
            const position = Vector3.TransformCoordinates(boundingBoxCenter, translation);
            sketchPlaneVisualization.position = new Vector3(position.x, position.y, position.z);
            const sketchNormal = new Vector3(0, 0, 0);
            if (sketch.normal === eAxis.x) {
                sketchNormal.set(1, 0, 0);
            } else if (sketch.normal === eAxis.y) {
                sketchNormal.set(0, 1, 0);
            } else {
                sketchNormal.set(0, 0, 1);
            }
            const rotationVector = Vector3.TransformCoordinates(
                sketchNormal,
                sketchMesh.computeWorldMatrix().getRotationMatrix()
            );
            rotationVector.z = rotationVector.z * -1;
            const resultQuaternion = new Quaternion();
            Quaternion.FromUnitVectorsToRef(rotationVector, new Vector3(0, 0, 1), resultQuaternion);
            sketchPlaneVisualization.rotationQuaternion = resultQuaternion;

            //uvMapVisualization.rotation = Vector3.RotationFromAxis()
            sketchPlaneVisualization.material = coloredMaterial;

            sketchPlaneVisualization.bakeCurrentTransformIntoVertices();
            return sketchPlaneVisualization;
        } else {
            console.warn(
                'Sketch visualization plane cannot be generated, please check direction and position of sketchpoints.'
            );
            return null;
        }
    }

    public updateConnectorScaling(node: SpaceNode) {
        if (node instanceof SketchNode) {
            node.paths.forEach(path => {
                path.controlPoints.forEach(controlPoint => {
                    this.viewer.rendererManager.controlPointRenderer.updateScale(controlPoint);
                });
            });
        } else {
            if (node.connectors) {
                node.connectors.forEach(connector => {
                    this.viewer.rendererManager.connector.updateScale(connector);
                });
            }
            const childNodes = node.getChildren();
            if (childNodes) {
                childNodes.forEach(child => {
                    if (child instanceof SpaceNode) {
                        this.updateConnectorScaling(child);
                    }
                });
            }
        }
    }

    public calculateSketchBoundingBox(node: SketchNode) {
        const firstPoint = node.paths[0]._points[0];
        const minVector: Vector3 = new Vector3(firstPoint.x, firstPoint.y, firstPoint.z);
        const maxVector: Vector3 = new Vector3(firstPoint.x, firstPoint.y, firstPoint.z);
        node.paths.forEach(path => {
            path._points.forEach(point => {
                if (point.x < minVector.x) {
                    minVector.x = point.x;
                }
                if (point.y < minVector.y) {
                    minVector.y = point.y;
                }
                if (point.z < minVector.z) {
                    minVector.z = point.z;
                }
                if (point.x > maxVector.x) {
                    maxVector.x = point.x;
                }
                if (point.y > maxVector.y) {
                    maxVector.y = point.y;
                }
                if (point.z > maxVector.z) {
                    maxVector.z = point.z;
                }
            });
        });
        return new BoundingBox(minVector, maxVector);
    }
}
